import React, { useEffect, useState } from 'react';
import {
  Alert,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { Add, Edit, Save } from '@mui/icons-material';
import { shiftApi } from '../../../api/shiftApi';
import { toast } from 'react-toastify';

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

type DayConfig = {
  day: string;
  isWorkingDay: boolean;
  isWeekOff: boolean;
  isHalfDay: boolean;
  weekOffWeeks: number[];
};

type WorkWeekRow = {
  id: string;
  name: string;
  isActive: boolean;
  days: DayConfig[];
  weekOffRules: Array<{ day: string; weeks: number[] }>;
};

const createDefaultDays = (): DayConfig[] =>
  daysOfWeek.map((day) => ({
    day,
    isWorkingDay: !['Saturday', 'Sunday'].includes(day),
    isWeekOff: ['Saturday', 'Sunday'].includes(day),
    isHalfDay: false,
    weekOffWeeks: [],
  }));

const mapApiDays = (apiDays: any[] = [], weekOffRules: Array<{ dayName?: string; day?: string; weeks?: number[]; weekOfMonth?: number }> = []): DayConfig[] => {
  const ruleMap = weekOffRules.reduce((acc, rule) => {
    const key = (rule.dayName || rule.day || '').toLowerCase();
    if (!key) return acc;

    if (!acc[key]) acc[key] = [];

    if (Array.isArray(rule.weeks)) {
      rule.weeks.forEach((week) => {
        if (!acc[key].includes(week)) acc[key].push(week);
      });
    } else if (typeof rule.weekOfMonth === 'number') {
      if (!acc[key].includes(rule.weekOfMonth)) acc[key].push(rule.weekOfMonth);
    }

    return acc;
  }, {} as Record<string, number[]>);

  return daysOfWeek.map((day) => {
    const dayItem = apiDays.find((d: any) => (d.dayName || '').toLowerCase() === day.toLowerCase());
    const isWorkingDay = dayItem?.isWorkingDay ?? !['Saturday', 'Sunday'].includes(day);
    const isHalfDay = dayItem?.isHalfDay ?? false;
    const weekOffWeeks = ruleMap[day.toLowerCase()] || [];
    return {
      day,
      isWorkingDay,
      isWeekOff: !isWorkingDay,
      isHalfDay,
      weekOffWeeks,
    };
  });
};

const WorkWeekForm: React.FC = () => {
  const [workWeeks, setWorkWeeks] = useState<WorkWeekRow[]>([]);

  const [days, setDays] = useState<DayConfig[]>(createDefaultDays());
  const [workWeekId, setWorkWeekId] = useState<string | null>(null);
  const [workWeekName, setWorkWeekName] = useState('Default Work Week');
  const [isActive, setIsActive] = useState(true);

  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);

  const loadWorkWeeks = async () => {
    setLoading(true);
    try {
      const list = await shiftApi.getWorkWeekConfiguration();
      const mapped: WorkWeekRow[] = (list || []).map((item: any) => ({
        id: item.id,
        name: item.name || 'Default Work Week',
        isActive: item.isActive ?? true,
        weekOffRules: (item.weekOffRules || []).reduce((acc: Array<{ day: string; weeks: number[] }>, rule: any) => {
          const dayName = rule.dayName || rule.day || '';
          const week = Number(rule.weekOfMonth || rule.week || rule.weekIndex || rule.weekNumber);
          if (!dayName || Number.isNaN(week)) return acc;
          const existing = acc.find((x) => x.day.toLowerCase() === dayName.toLowerCase());
          if (existing) {
            if (!existing.weeks.includes(week)) existing.weeks.push(week);
            return acc;
          }
          acc.push({ day: dayName, weeks: [week] });
          return acc;
        }, []),
        days: mapApiDays(item.days || [], item.weekOffRules || []),
      }));
      setWorkWeeks(mapped);
    } catch {
      setWorkWeeks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkWeeks();
  }, []);

  const openCreateDialog = () => {
    setIsCreateMode(true);
    setWorkWeekId(null);
    setWorkWeekName(`Work Week ${workWeeks.length + 1}`);
    setIsActive(true);
    setDays(createDefaultDays());
    setOpenEdit(true);
  };

  const openEditDialog = (row: WorkWeekRow) => {
    setIsCreateMode(false);
    setWorkWeekId(row.id);
    setWorkWeekName(row.name);
    setIsActive(row.isActive);
    setDays(row.days);
    setOpenEdit(true);
  };

  const updateDay = (day: string, patch: Partial<DayConfig> | ((prev: DayConfig) => DayConfig)) => {
    setDays((prev) =>
      prev.map((item) =>
        item.day === day
          ? typeof patch === 'function'
            ? patch(item)
            : { ...item, ...patch }
          : item
      )
    );
  };

  const handleWorkingDayChange = (day: string, checked: boolean) => {
    updateDay(day, {
      isWorkingDay: checked,
      isWeekOff: !checked,
      isHalfDay: checked ? false : false,
      weekOffWeeks: checked ? [] : [],
    });
  };

  const handleWeekOffChange = (day: string, checked: boolean) => {
    updateDay(day, {
      isWeekOff: checked,
      isWorkingDay: !checked,
      isHalfDay: checked ? false : false,
      weekOffWeeks: checked ? [] : [],
    });
  };

  const handleHalfDayChange = (day: string, checked: boolean) => {
    updateDay(day, {
      isHalfDay: checked,
      isWorkingDay: checked ? true : undefined,
      isWeekOff: checked ? false : undefined,
    });
  };

  const toggleWeekOffWeek = (day: string, week: number) => {
    updateDay(day, (prev) => {
      const weekOffWeeks = prev.weekOffWeeks.includes(week)
        ? prev.weekOffWeeks.filter((w) => w !== week)
        : [...prev.weekOffWeeks, week];

      return {
        ...prev,
        isWorkingDay: true,
        isWeekOff: false,
        isHalfDay: false,
        weekOffWeeks,
      };
    });
  };

  const handleSubmit = async () => {
    const normalizedName = workWeekName.trim();

    if (!normalizedName) {
      toast.error('Please enter work week name');
      return;
    }

    const duplicateExists = workWeeks.some((w) => {
      if (!isCreateMode && workWeekId && w.id === workWeekId) return false;
      return w.name.trim().toLowerCase() === normalizedName.toLowerCase();
    });

    if (duplicateExists) {
      toast.error('Work week name already exists.');
      return;
    }

    const workingDays = days.filter((d) => d.isWorkingDay).map((d) => d.day);

    if (workingDays.length === 0) {
      toast.error('Please select at least one working day');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: normalizedName,
        isActive,
        days: days.map((d) => ({
          day: d.day,
          workingDay: d.isWorkingDay,
          weekOff: d.isWeekOff,
          halfDay: d.isHalfDay,
        })),
        weekOffRules: days
          .filter((d) => d.weekOffWeeks.length > 0)
          .map((d) => ({
            day: d.day,
            weeks: d.weekOffWeeks,
          })),
      };

      if (isCreateMode) {
        await shiftApi.saveWorkWeekConfiguration(payload);
      } else {
        if (!workWeekId) {
          throw new Error('Work week id is missing for update');
        }
        await shiftApi.updateWorkWeekConfiguration(workWeekId, payload);
      }

      await loadWorkWeeks();
      toast.success(isCreateMode ? 'Work week added successfully!' : 'Work week updated successfully!');
      setOpenEdit(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save work week');
    } finally {
      setSubmitting(false);
    }
  };

  // Show working weeks for each day, e.g., Working (1,3,5) if 2nd and 4th are week off
  const getDayBadge = (day: DayConfig) => {
    if (!day.isWorkingDay || day.isWeekOff) {
      return <Chip size="small" label="Non-working" color="error" />;
    }
    if (day.isHalfDay) {
      return <Chip size="small" label="Half-day" color="warning" />;
    }
    // If there are weekOffWeeks, show working weeks
    if (day.weekOffWeeks && day.weekOffWeeks.length > 0) {
      // Weeks 1-5
      const allWeeks = [1, 2, 3, 4, 5];
      const workingWeeks = allWeeks.filter(w => !day.weekOffWeeks.includes(w));
      return <Chip size="small" label={`(${workingWeeks.join(',')}) Working`} color="success" />;
    }
    return <Chip size="small" label="Working" color="success" />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Work Week</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}  sx={{ textTransform: 'none' }}>
            Add New Work Week
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>S.No</strong></TableCell>
                <TableCell><strong>Work Week Name</strong></TableCell>
                <TableCell><strong>Mon</strong></TableCell>
                <TableCell><strong>Tue</strong></TableCell>
                <TableCell><strong>Wed</strong></TableCell>
                <TableCell><strong>Thu</strong></TableCell>
                <TableCell><strong>Fri</strong></TableCell>
                <TableCell><strong>Sat</strong></TableCell>
                <TableCell><strong>Sun</strong></TableCell>
                <TableCell align="center"><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workWeeks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">No work week found. Click "Add New Work Week" to create one.</TableCell>
                </TableRow>
              ) : (
                workWeeks.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    {row.days.map((day) => (
                      <TableCell key={`${row.id}-${day.day}`}>{getDayBadge(day)}</TableCell>
                    ))}
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => openEditDialog(row)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="md" fullWidth>
          <DialogTitle>{isCreateMode ? 'Add New Work Week' : 'Update Work Week'}</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Work Week Name
              </Typography>
              <input
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #d0d0d0',
                  fontSize: 14,
                }}
                value={workWeekName}
                onChange={(e) => setWorkWeekName(e.target.value)}
                placeholder="Enter work week name"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure each day as Working Day, Week Off, or Half Day.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Half-day can be selected only for working days.
            </Alert>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Day</strong></TableCell>
                    <TableCell align="center"><strong>Working Day</strong></TableCell>
                    <TableCell align="center"><strong>Week Off</strong></TableCell>
                    <TableCell align="center"><strong>Half Day</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {days.map((item) => (
                    <TableRow key={item.day}>
                      <TableCell>{item.day}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={item.isWorkingDay}
                          onChange={(e) => handleWorkingDayChange(item.day, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={item.isWeekOff}
                          onChange={(e) => handleWeekOffChange(item.day, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={item.isHalfDay}
                          disabled={item.isWeekOff}
                          onChange={(e) => handleHalfDayChange(item.day, e.target.checked)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Week Off by Week of Month (e.g., 2nd & 4th Monday)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Day</strong></TableCell>
                      <TableCell align="center"><strong>1st</strong></TableCell>
                      <TableCell align="center"><strong>2nd</strong></TableCell>
                      <TableCell align="center"><strong>3rd</strong></TableCell>
                      <TableCell align="center"><strong>4th</strong></TableCell>
                      <TableCell align="center"><strong>5th</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {days.map((item) => {
                      const isDisabled = item.isWeekOff || !item.isWorkingDay;
                      return (
                        <TableRow key={`week-off-${item.day}`}>
                          <TableCell>{item.day}</TableCell>
                          {[1, 2, 3, 4, 5].map((week) => (
                            <TableCell key={`${item.day}-${week}`} align="center">
                              <Checkbox
                                checked={item.weekOffWeeks.includes(week)}
                                disabled={isDisabled}
                                onChange={() => toggleWeekOffWeek(item.day, week)}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEdit(false)} color="inherit" disabled={submitting} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}
              disabled={submitting}
              sx={{ textTransform: 'none' }}
            >
              {submitting ? 'Saving...' : isCreateMode ? 'Add Work Week' : 'Save Work Week'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default WorkWeekForm;
